/**
 * BUILD-7490 — Motor de evaluación de recomendaciones de Andy.
 * Helpers puros: no mutan timers ni el store. El naranja del board
 * (`TIMERPRODUCT_STATUS.CLOSE_TO_EXPIRE`) es la única regla de
 * “próximo a vencer”; no se recalcula el umbral de minutos.
 */

import { RECOMMENDATION_TRIGGERS, RECOMMENDATION_VARIANT, TIMERPRODUCT_STATUS } from '../constants.js'

/**
 * @param {Object|Array|null|undefined} timers
 * @returns {Array<Object>}
 */
export function listTimers(timers) {
    if (!timers) {
        return []
    }

    return Array.isArray(timers) ? timers : Object.values(timers)
}

/**
 * Normaliza el status numérico. Misma semántica que `getHomeWidgetData` (`==`).
 *
 * @param {Object|null|undefined} timer
 * @returns {number|null}
 */
export function resolveTimerStatus(timer) {
    if (!timer || timer.status == null || timer.status === '') {
        return null
    }

    const status = Number(timer.status)
    return Number.isFinite(status) ? status : null
}

/**
 * @param {Object|null|undefined} timer
 * @returns {boolean}
 */
export function isExpiredTimer(timer) {
    return resolveTimerStatus(timer) === TIMERPRODUCT_STATUS.EXPIRED
}

/**
 * Timers de la columna Desechar (`status === EXPIRED`).
 * Misma regla que el trigger y que `getHomeWidgetData.to_delete`.
 *
 * @param {Object|Array|null|undefined} timers
 * @returns {Array<Object>}
 */
export function collectExpiredTimers(timers) {
    const items = listTimers(timers)
    const expired = []

    for (let i = 0; i < items.length; i++) {
        const timer = items[i]
        if (timer && isExpiredTimer(timer)) {
            expired.push(timer)
        }
    }

    return expired
}

/**
 * Misma condición que pinta la tarjeta en naranja (`status-3`).
 *
 * @param {Object|null|undefined} timer
 * @returns {boolean}
 */
export function isCloseToExpireTimer(timer) {
    return resolveTimerStatus(timer) === TIMERPRODUCT_STATUS.CLOSE_TO_EXPIRE
}

/**
 * Timer activo que aún no está naranja ni expirado (AVAILABLE / PREPARATION).
 *
 * @param {Object|null|undefined} timer
 * @returns {boolean}
 */
export function isHealthyActiveTimer(timer) {
    const status = resolveTimerStatus(timer)
    if (status == null) {
        return false
    }

    return status > TIMERPRODUCT_STATUS.EXPIRED && status !== TIMERPRODUCT_STATUS.CLOSE_TO_EXPIRE
}

/**
 * @param {Object|null|undefined} timer
 * @returns {string|null}
 */
export function resolveTimerProductId(timer) {
    if (!timer || timer.reference_id == null || timer.reference_id === '') {
        return null
    }

    return String(timer.reference_id)
}

/**
 * Agrupa timers por producto para decidir si hay backup activo.
 *
 * @param {Object|Array|null|undefined} timers
 * @returns {{ expiredCount: number, hasCloseToExpireWithoutBackup: boolean }}
 */
export function summarizeRecommendationTimers(timers) {
    const buckets = Object.create(null)
    let expiredCount = 0
    const items = listTimers(timers)

    for (let i = 0; i < items.length; i++) {
        const timer = items[i]
        if (!timer) {
            continue
        }

        if (isExpiredTimer(timer)) {
            expiredCount += 1
        }

        const productId = resolveTimerProductId(timer)
        if (!productId) {
            continue
        }

        if (!buckets[productId]) {
            buckets[productId] = {
                closeToExpire: 0,
                healthyActive: 0
            }
        }

        if (isCloseToExpireTimer(timer)) {
            buckets[productId].closeToExpire += 1
        } else if (isHealthyActiveTimer(timer)) {
            buckets[productId].healthyActive += 1
        }
    }

    const productIds = Object.keys(buckets)
    let hasCloseToExpireWithoutBackup = false

    for (let j = 0; j < productIds.length; j++) {
        const bucket = buckets[productIds[j]]
        if (bucket.closeToExpire > 0 && bucket.healthyActive === 0) {
            hasCloseToExpireWithoutBackup = true
            break
        }
    }

    return {
        expiredCount,
        hasCloseToExpireWithoutBackup
    }
}

/**
 * Productos naranja sin backup saludable (AVAILABLE / PREPARATION).
 * Misma regla que el trigger ADD_PRODUCTS; no muta timers.
 *
 * @param {Object|Array|null|undefined} timers
 * @returns {Array<string>}
 */
export function collectRestockProductIds(timers) {
    const buckets = Object.create(null)
    const order = []
    const items = listTimers(timers)

    for (let i = 0; i < items.length; i++) {
        const timer = items[i]
        if (!timer) {
            continue
        }

        const productId = resolveTimerProductId(timer)
        if (!productId) {
            continue
        }

        if (!buckets[productId]) {
            buckets[productId] = {
                closeToExpire: 0,
                healthyActive: 0
            }
            order.push(productId)
        }

        if (isCloseToExpireTimer(timer)) {
            buckets[productId].closeToExpire += 1
        } else if (isHealthyActiveTimer(timer)) {
            buckets[productId].healthyActive += 1
        }
    }

    const restockProductIds = []
    for (let j = 0; j < order.length; j++) {
        const productId = order[j]
        const bucket = buckets[productId]
        if (bucket.closeToExpire > 0 && bucket.healthyActive === 0) {
            restockProductIds.push(productId)
        }
    }

    return restockProductIds
}

/**
 * Resuelve el producto en el catálogo (id string/number) para el wizard.
 *
 * @param {Object|null|undefined} productsMap
 * @param {string|number} productId
 * @returns {Object|null}
 */
function resolveProductFromMap(productsMap, productId) {
    if (!productsMap || productId == null || productId === '') {
        return null
    }

    const direct = productsMap[productId]
    if (direct) {
        return direct
    }

    const asString = productsMap[String(productId)]
    if (asString) {
        return asString
    }

    const numericId = Number(productId)
    if (Number.isFinite(numericId) && productsMap[numericId]) {
        return productsMap[numericId]
    }

    return null
}

/**
 * Productos del catálogo que hay que reponer (naranja sin backup).
 * Omite ids sin ficha: el wizard necesita `states`.
 *
 * @param {Object|Array|null|undefined} timers
 * @param {Object|null|undefined} productsMap
 * @returns {Array<Object>}
 */
export function collectRestockProducts(timers, productsMap) {
    const ids = collectRestockProductIds(timers)
    const products = []

    for (let i = 0; i < ids.length; i++) {
        const product = resolveProductFromMap(productsMap, ids[i])
        if (product && product.id != null && product.states) {
            products.push(product)
        }
    }

    return products
}

/**
 * ADD/COMBINED solo si hay ficha con `states` (el wizard no puede abrir ids huérfanos).
 * Sin `productsMap` se evalúa solo con timers (tests del motor).
 *
 * @param {Object|Array|null|undefined} timers
 * @param {Object|null|undefined} productsMap
 * @returns {boolean}
 */
export function hasActionableRestockProducts(timers, productsMap) {
    if (productsMap === undefined) {
        return summarizeRecommendationTimers(timers).hasCloseToExpireWithoutBackup
    }

    return collectRestockProducts(timers, productsMap).length > 0
}

/**
 * Elige una sola variante según prioridad del pitch:
 * 1) Combinada  2) Eliminar expirados  3) Añadir productos.
 *
 * @param {Object|Array|null|undefined} timers
 * @param {Object} [triggers]
 * @param {Object} [productsMap]
 * @returns {{ variant: string, expiredCount: number, hasCloseToExpireWithoutBackup: boolean }}
 */
export function evaluateRecommendationTriggers(timers, triggers = RECOMMENDATION_TRIGGERS, productsMap) {
    const snapshot = summarizeRecommendationTimers(timers)
    const threshold = Number(triggers && triggers.expiredThreshold)
    const expiredThreshold = Number.isFinite(threshold) ? threshold : RECOMMENDATION_TRIGGERS.expiredThreshold
    const hasExpiredOverflow = snapshot.expiredCount > expiredThreshold
    const hasAddTrigger = hasActionableRestockProducts(timers, productsMap)
    const eligible = {
        [RECOMMENDATION_VARIANT.COMBINED]: hasExpiredOverflow && hasAddTrigger,
        [RECOMMENDATION_VARIANT.DELETE_EXPIRED]: hasExpiredOverflow,
        [RECOMMENDATION_VARIANT.ADD_PRODUCTS]: hasAddTrigger
    }
    const priority = triggers && Array.isArray(triggers.priority) && triggers.priority.length ? triggers.priority : RECOMMENDATION_TRIGGERS.priority
    const variant = priority.find((item) => eligible[item]) || ''

    return {
        variant,
        expiredCount: snapshot.expiredCount,
        hasCloseToExpireWithoutBackup: snapshot.hasCloseToExpireWithoutBackup
    }
}
